import React, { useState } from 'react';

import {
  Accordion,
  Breakpoints,
  TextTag,
  TextAppearance,
  TextColor,
  TextCustom,
  TextLineHeight,
  TextSize,
  TextWeight,
  CTA,
  useTheme,
} from '@vw-marketing/us-components';

import { applyTextTransformations } from '../../../../shared/utils/applyTextTransformations';
import { getUserPreferredVehiclePrivileges } from '../../../../shared/utils/general';
import {
  StyledAccordionContainer,
  StyledAccordionInformationsCopyToClipboardContainer,
  StyledInformationContainer,
  StyledUserVehicleInformationContainer,
  StyledVehicleInteractionCardModelHeaderButton,
  StyledVehicleInteractiveCard,
  StyledVehicleInteractiveCardHeader,
  StyledVehicleInteractiveCardHeaderTitleSubtitle,
  StyledVehicleInteractiveCardSpecialButton,
  StyledVehicleInteractiveCardSpecialButtonLabel,
  StyledVehicleInteractiveCardSpecialButtonLabelControl,
} from './styled';
import { useClipboard } from '../../../../shared/utils/copyToClipboard';
import { useMyVwContext } from '../../../../shared/hooks/use-my-vw-context';
import { useTrackingManager } from '../../../../shared/hooks/use-tracking-manager';
import { useUser } from '../../../../app/providers/UserContext';
import { useVehicle } from '../../../../app/providers/VehicleContext';
import { onVWBasicLinkClickWithProcessEvent, onVWBasicAccordionOpenClickEvent } from '../../../../shared/tagging/tagging-helpers';
import Clipboard from '../../../../shared/components/icons/Clipboard';
import Pencil from '../../../../shared/components/icons/Pencil';
import RemoveVehicle from '../remove-vehicle';
import Trash from '../../../../shared/components/icons/Trash';

interface VehicleInteractiveCardProps {
  onOpenNickname?: () => void;
  onOpenRemoveVehicle?: () => void;
}

const VehicleInteractiveCard = ({ onOpenNickname, onOpenRemoveVehicle }: VehicleInteractiveCardProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { copyToClipboard } = useClipboard();
  const { translationHandler } = useMyVwContext();
  const { userAccountPrivileges } = useUser();
  const { userPreferredVehicle } = useVehicle();
  const trackingManager = useTrackingManager();
  const theme = useTheme();

  const handleClick = () => {
    try {
      onVWBasicAccordionOpenClickEvent(trackingManager, {
        Name: 'Vehicle Management',
        StepName: 'Vehicle Details',
        StepNumber: '5',
      });
    } catch (error) {
      console.warn('Error tracking accordion open click:', error);
    }
    setIsOpen(!isOpen);
  };

  const handleNicknameClick = () => {
    try {
      const linkName = userPreferredVehicle?.vehicleNickname ? 'Edit Nickname' : 'Add Nickname';
      onVWBasicLinkClickWithProcessEvent(trackingManager, {
        Name: 'Vehicle Management',
        StepName: 'Vehicle Details',
        StepNumber: '5',
      }, {
        name: linkName,
      });
    } catch (error) {
      console.warn('Error tracking nickname link click:', error);
    }
    onOpenNickname?.();
  };

  const handleCopyToClipboard = async (text: string) => {
    await copyToClipboard(text);
  };

  const userPrivileges =
    getUserPreferredVehiclePrivileges(
      userPreferredVehicle?.vehicleId,
      userAccountPrivileges,
    )?.roles[0] ?? '';

  const connectivyCarrier =
    userPreferredVehicle?.ocuSim?.defaultMno === 'TMO'
      ? 'T-Mobile'
      : userPreferredVehicle?.ocuSim?.defaultMno;

  const vehicleIdentifiers: any[] = [
    {
      label: applyTextTransformations(
        translationHandler(
          'vehicleManagement.interactive.card.vehicle.header.VIN',
        ),
        ['VIN'],
      ),
      value: userPreferredVehicle?.vin,
      ariaLabel: `Copy ${translationHandler('vehicleManagement.interactive.card.vehicle.header.VIN')} to clipboard`,
    },
    {
      label: applyTextTransformations(
        translationHandler(
          'vehicleManagement.interactive.card.vehicle.accordion.IMEI.Number',
        ),
        ['IMEI'],
      ),
      value: userPreferredVehicle?.ocuSim?.imei,
      ariaLabel: `Copy ${translationHandler('vehicleManagement.interactive.card.vehicle.accordion.IMEI.Number')} to clipboard`,
    },
    {
      label: applyTextTransformations(
        translationHandler(
          'vehicleManagement.interactive.card.vehicle.accordion.Vehicle.MSISDN',
        ),
        ['MSISDN'],
      ),
      value: userPreferredVehicle?.ocuSim?.ocuSimProfile?.msisdn,
      ariaLabel: `Copy ${translationHandler('vehicleManagement.interactive.card.vehicle.accordion.Vehicle.MSISDN')} to clipboard`,
    },
    {
      label: translationHandler(
        'vehicleManagement.interactive.card.vehicle.accordion.Connectivity.Carrier',
      ),
      value: applyTextTransformations(connectivyCarrier ?? '', [
        connectivyCarrier ?? '',
      ]),
      ariaLabel: `Copy ${translationHandler('vehicleManagement.interactive.card.vehicle.accordion.Connectivity.Carrier')} to clipboard`,
    },
  ];

  return (
    <StyledVehicleInteractiveCard>
      <StyledVehicleInteractiveCardHeader>
        <StyledVehicleInteractiveCardHeaderTitleSubtitle>
          {userPrivileges === 'SU' && (
            <TextCustom
              appearance={TextAppearance.label100}
              style={{
                [Breakpoints.default]: {
                  fontSize: TextSize.TextSize12,
                  color: TextColor.secondary,
                  fontWeight: TextWeight.bold,
                  lineHeight: TextLineHeight.TextLineHeight16,
                },
                [Breakpoints.b1600]: {
                  fontSize: TextSize.TextSize14,
                },
              }}
            >
              {translationHandler(
                'vehicleManagement.interactive.card.vehicle.header.additional.user',
              )}
            </TextCustom>
          )}
          <a
            className="mobile-edit-button-anchor"
            onClick={handleNicknameClick}
          >
            <TextCustom
              tag={TextTag.p}
              appearance={TextAppearance.headline200}
              style={{
                [Breakpoints.default]: {
                  fontSize: TextSize.TextSize20,
                  fontWeight: TextWeight.bold,
                  color: TextColor.primary,
                  lineHeight: TextLineHeight.TextLineHeight24,
                },
                [Breakpoints.b1600]: {
                  fontSize: TextSize.TextSize20,
                },
              }}
            >
              {userPreferredVehicle?.vehicleNickname ? (
                (
                  <>
                    {applyTextTransformations(
                      userPreferredVehicle?.vehicleNickname,
                      [userPreferredVehicle?.vehicleNickname],
                    )}
                  </>
                ) || ''
              ) : (
                <>
                  <div>
                    {applyTextTransformations(
                      `${userPreferredVehicle?.modelYear ?? ''} ${userPreferredVehicle?.modelName ?? ''}`,
                      [
                        userPreferredVehicle?.modelYear ?? '',
                        userPreferredVehicle?.modelName ?? '',
                      ],
                    )}
                  </div>
                </>
              )}
              <div className="mobile-edit-button-container">
                <Pencil iconSize={16} />
              </div>
            </TextCustom>
          </a>
          <TextCustom
            appearance={TextAppearance.copy100}
            style={{
              [Breakpoints.default]: {
                fontSize: TextSize.TextSize14,
                color: TextColor.secondary,
                lineHeight: TextLineHeight.TextLineHeight24,
              },
              [Breakpoints.b1600]: {
                fontSize: TextSize.TextSize16,
              },
            }}
          >
            {userPreferredVehicle?.vehicleNickname ? (
              <>
                {applyTextTransformations(
                  `${userPreferredVehicle?.modelYear ?? ''} ${userPreferredVehicle?.modelName ?? ''}`,
                  [
                    userPreferredVehicle?.modelYear ?? '',
                    userPreferredVehicle?.modelName ?? '',
                  ],
                )}
              </>
            ) : (
              <>
                {applyTextTransformations(
                  translationHandler(
                    'vehicleManagement.interactive.card.vehicle.header.VIN',
                  ),
                  ['VIN'],
                )}{' '}
                {userPreferredVehicle?.vin}
              </>
            )}
          </TextCustom>
        </StyledVehicleInteractiveCardHeaderTitleSubtitle>
        <div className="desktop-edit-button-container">
          <StyledVehicleInteractionCardModelHeaderButton>
            <CTA
              tag="button"
              emphasis="tertiary"
              icon={<Pencil />}
              iconPosition="start"
              onClick={handleNicknameClick}
            >
              {userPreferredVehicle?.vehicleNickname
                ? translationHandler(
                  'vehicleManagement.interactive.card.vehicle.header.edit.nickname',
                )
                : translationHandler(
                  'vehicleManagement.interactive.card.vehicle.header.add.nickname',
                )}
            </CTA>
          </StyledVehicleInteractionCardModelHeaderButton>
        </div>
      </StyledVehicleInteractiveCardHeader>
      <StyledAccordionContainer>
        <Accordion
          id="Vehicle_Identifiers_Accordion"
          headline={translationHandler('vehicleManagement.interactive.card.vehicle.header.vehicle.identifiers')}
          onClick={handleClick}
          isOpen={isOpen}
        >
          <StyledUserVehicleInformationContainer>
            {vehicleIdentifiers.map((item, key: number) => (
              <StyledInformationContainer key={`Identifier-${item.label}-${key + 1}`}>
                <TextCustom
                  appearance={TextAppearance.copy100}
                  style={{
                    [Breakpoints.default]: {
                      fontSize: TextSize.TextSize14,
                      color: TextColor.secondary,
                      lineHeight: TextLineHeight.TextLineHeight24,
                    },
                    [Breakpoints.b1600]: {
                      fontSize: TextSize.TextSize16,
                    },
                  }}
                >
                  {item.label}
                </TextCustom>
                <StyledAccordionInformationsCopyToClipboardContainer
                  aria-label={item.ariaLabel}
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    handleCopyToClipboard(
                      typeof item.value === 'string'
                        ? item.value
                        : connectivyCarrier || '',
                    )
                  }
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleCopyToClipboard(
                        typeof item.value === 'string'
                          ? item.value
                          : connectivyCarrier || '',
                      );
                    }
                  }}
                >
                  <TextCustom
                    appearance={TextAppearance.copy100}
                    style={{
                      [Breakpoints.default]: {
                        fontSize: TextSize.TextSize14,
                        lineHeight: TextLineHeight.TextLineHeight24,
                      },
                      [Breakpoints.b1600]: {
                        fontSize: TextSize.TextSize16,
                      },
                    }}
                  >
                    {item.value || ''}
                  </TextCustom>
                  <Clipboard />
                </StyledAccordionInformationsCopyToClipboardContainer>
              </StyledInformationContainer>
            ))}
          </StyledUserVehicleInformationContainer>
        </Accordion>
      </StyledAccordionContainer>
      <RemoveVehicle onOpen={onOpenRemoveVehicle}>
        <StyledVehicleInteractiveCardSpecialButton>
          <StyledVehicleInteractiveCardSpecialButtonLabelControl>
            <StyledVehicleInteractiveCardSpecialButtonLabel>
              {translationHandler(
                'vehicleManagement.interactive.card.vehicle.special.button',
              )}
            </StyledVehicleInteractiveCardSpecialButtonLabel>
            <Trash color={theme.colors.signal.error} />
          </StyledVehicleInteractiveCardSpecialButtonLabelControl>
        </StyledVehicleInteractiveCardSpecialButton>
      </RemoveVehicle>
    </StyledVehicleInteractiveCard>
  );
};

export default VehicleInteractiveCard;